/**
 * Tier 1 image-URL cache.
 *
 * Persists Supabase signed URLs to IndexedDB (via idb-keyval) and mirrors
 * them in an in-memory Map for synchronous reads. The goal is to avoid
 * re-calling `createSignedUrl` for an image the user has already seen
 * within the URL's TTL — saves ~80% of signed-URL API calls on Supabase
 * and makes thumbnails appear instantly on repeat opens.
 *
 * Key format: `${bucket}|${path}|${transformKey}` — purge by prefix to
 * invalidate a single bucket (e.g. on logout or when a tryon-results row
 * expires after 24h).
 */
import { get, set, del, keys, createStore } from "idb-keyval";

export type CachedUrl = { url: string; expiresAt: number };

const store = createStore("fitaura-img-cache", "signed-urls");
const mem = new Map<string, CachedUrl>();
let hydrated = false;
let hydrating: Promise<void> | null = null;

export async function hydrate(): Promise<void> {
  if (hydrated) return;
  if (hydrating) return hydrating;
  hydrating = (async () => {
    try {
      const all = await keys(store);
      const now = Date.now();
      await Promise.all(
        all.map(async (k) => {
          const v = (await get(k, store)) as CachedUrl | undefined;
          if (!v) return;
          if (v.expiresAt <= now) {
            await del(k, store);
            return;
          }
          mem.set(String(k), v);
        }),
      );
    } catch {
      // IDB may be unavailable (private mode etc.) — fall back to memory only.
    }
    hydrated = true;
  })();
  return hydrating;
}

export function getCachedUrl(key: string): CachedUrl | null {
  const v = mem.get(key);
  if (!v) return null;
  if (v.expiresAt <= Date.now()) {
    mem.delete(key);
    void del(key, store).catch(() => {});
    return null;
  }
  return v;
}

export function setCachedUrl(key: string, url: string, expiresAt: number): void {
  const entry: CachedUrl = { url, expiresAt };
  mem.set(key, entry);
  void set(key, entry, store).catch(() => {});
}

/** Purge by exact key or by prefix (e.g. "tryon-results|"). */
export async function purge(prefix?: string): Promise<void> {
  if (!prefix) {
    mem.clear();
    try {
      const all = await keys(store);
      await Promise.all(all.map((k) => del(k, store)));
    } catch {}
    return;
  }
  for (const k of Array.from(mem.keys())) {
    if (k.startsWith(prefix)) mem.delete(k);
  }
  try {
    const all = await keys(store);
    await Promise.all(
      all
        .filter((k) => String(k).startsWith(prefix))
        .map((k) => del(k, store)),
    );
  } catch {}
}

// Kick off hydration immediately on module load — non-blocking.
void hydrate();
